import {
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    MenuItem,
    TextField,
    Tooltip
} from '@material-ui/core';
import { InfoIcon } from '../../../../icons';
import { Autocomplete } from '@material-ui/lab';
import FormSection from '../../../../components/atom/FormSection';
import PriceRuleAutoComplete from '../../../../components/atom/PriceRuleAutoComplete';
import FormikInput from '../../../../components/atom/FormikInput';
import { useNotify } from '../../../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../../../hooks/useSwaggerClient';
import { activityAutocomplete, tagAutocomplete, chainAutocomplete } from '../../../../autocomplete';
import AutocompleteSelect from '../../../../components/atom/AutocompleteSelect';
import SortableAutocomplete from '../../../../components/atom/SortableAutocomplete';

const WIDGET_TUTORIAL_URL =
    'https://s3-us-west-1.amazonaws.com/activiter-staging/widget/integrate/index.html';

export default function ClientEditClientSettings({ values, setFieldValue, errors }) {
    const [locations] = useSwaggerClient('terminals', 'getTerminalLocations', {}, [], ls =>
        ls.map(({ id, display_name }) => ({ id, name: display_name }))
    );

    const { notifyInfo } = useNotify();
    const uploadDocument = async event => {
        if (!event.target.files || !event.target.files.length) {
            return null;
        }
        const f = event.target.files[0];
        notifyInfo(`Upload started for agreement`);
        const fileName = await doSwaggerCall('files', 'uploadFile', {
            file: f
        }).then(res => {
            notifyInfo(`Upload finished for agreement`);
            return res.url;
        });
        return fileName;
    };

    return (
        <FormSection title="Client settings">
            {values.client_key && (
                <Grid item xs={12}>
                    <TextField
                        label="Client key (used for widget setup)"
                        fullWidth
                        value={values.client_key}
                        disabled
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="View widget integration tutorial">
                                        <IconButton
                                            size="small"
                                            component="a"
                                            href={WIDGET_TUTORIAL_URL}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <InfoIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            )
                        }}
                    />
                </Grid>
            )}
            <FormikInput
                xs={6}
                name="customBookingPrefix"
                label="Custom booking prefix"
                type="text"
            />
            <Grid item xs={6}>
                <TextField
                    label="Monthly fee for client (usd)"
                    fullWidth
                    type="number"
                    inputProps={{ step: 0.01, min: 0 }}
                    value={values.monthly_fee ? values.monthly_fee : 0}
                    onChange={ev => setFieldValue('monthly_fee', parseFloat(ev.target.value))}
                />
            </Grid>
            <Grid item xs={12} sm={12}>
                <TextField
                    label="Legacy payment method (if there is any)"
                    fullWidth
                    type="text"
                    value={values.payment_method}
                    disabled
                />
            </Grid>
            {values.payment_method.includes('terminal') && (
                <Grid item xs={12} sm={12}>
                    <Autocomplete
                        fullWidth
                        options={locations ?? []}
                        filterSelectedOptions
                        getOptionLabel={option => option.name}
                        onChange={(_, newValue) => setFieldValue('location', newValue)}
                        value={values.location}
                        renderInput={params => <TextField {...params} label="Terminal location" />}
                    />
                </Grid>
            )}
            <Grid item xs={12} sm={12}>
                <PriceRuleAutoComplete
                    type="tag"
                    fullWidth
                    label="Tags"
                    value={values.tags}
                    setValue={newValue => setFieldValue('tags', newValue)}
                />
            </Grid>
            <Grid item xs={12} sm={12}>
                <AutocompleteSelect
                    initCall={tagAutocomplete.init}
                    autocompleteCall={tagAutocomplete.search}
                    value={values.featured_tags.map(e => e.id)}
                    setValue={(newValue, resolveFromCache) => {
                        setFieldValue(
                            'featured_tags',
                            newValue.map(e => resolveFromCache(e))
                        );
                    }}
                    multiple
                >
                    <TextField label="Featured activity tags" />
                </AutocompleteSelect>
            </Grid>
            <Grid item xs={12} sm={12}>
                <PriceRuleAutoComplete
                    type="island"
                    fullWidth
                    label="Default island(s)"
                    value={values.defaultIsland}
                    setValue={newValue => setFieldValue('defaultIsland', newValue)}
                />
                {errors.restrictedToOneIsland && (
                    <p className="mt-1" style={{ color: '#f44336', fontSize: '12px' }}>
                        {errors.restrictedToOneIsland}
                    </p>
                )}
            </Grid>
            <Grid item xs={12} sm={12}>
                <PriceRuleAutoComplete
                    type="island"
                    fullWidth
                    label="Visible island(s) in filter (empty = all)"
                    value={values.visibleIslands}
                    setValue={newValue => setFieldValue('visibleIslands', newValue)}
                />
            </Grid>
            <Grid item xs={12} sm={12}>
                <TextField
                    select
                    fullWidth
                    label="Hide activities without timeslot"
                    value={values.hideActivitiesWithoutTimeslot}
                    onChange={e => setFieldValue('hideActivitiesWithoutTimeslot', e.target.value)}
                    helperText={
                        "Drives the client site's 'show unavailable activities' filter. " +
                        'The always/never options force it and hide the toggle.'
                    }
                >
                    <MenuItem value="default_on">Guest can pick - hidden by default</MenuItem>
                    <MenuItem value="default_off">Guest can pick - shown by default</MenuItem>
                    <MenuItem value="always">Always hide (no toggle)</MenuItem>
                    <MenuItem value="never">Always show (no toggle)</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={12} sm={12}>
                <SortableAutocomplete
                    values={values.featured_activities}
                    initCall={activityAutocomplete.init}
                    autocompleteCall={activityAutocomplete.search}
                    setFieldValue={newValue => setFieldValue('featured_activities', newValue)}
                    label="Featured activities"
                    droppableId="featured_activities"
                />
            </Grid>
            <Grid item xs={12} sm={12}>
                <AutocompleteSelect
                    initCall={chainAutocomplete.init}
                    autocompleteCall={chainAutocomplete.search}
                    value={values.chains}
                    setValue={newValue => {
                        setFieldValue('chains', newValue);
                    }}
                    multiple
                >
                    <TextField label="Chain" />
                </AutocompleteSelect>
            </Grid>
            <Grid item xs={12} sm={12}>
                <InputLabel style={{ marginBottom: '3px' }} shrink>
                    Agreement:
                </InputLabel>
                {values.agreement && (
                    <>
                        <a href={values.agreement} target="_blank" rel="noreferrer">
                            {values.agreement}
                        </a>
                        <br />
                        <Button
                            variant="contained"
                            component="span"
                            size="small"
                            onClick={() => {
                                setFieldValue('agreement', null);
                            }}
                        >
                            Remove
                        </Button>
                    </>
                )}
                <label htmlFor="contained-button-file">
                    <Input
                        id="contained-button-file"
                        type="file"
                        style={{ display: 'none' }}
                        onChange={async ev => {
                            await uploadDocument(ev).then(res => {
                                setFieldValue('agreement', res);
                            });
                        }}
                    />
                    <Button variant="contained" component="span" size="small">
                        Upload
                    </Button>
                </label>
            </Grid>
            <Grid item xs={12} sm={12}>
                <FormControlLabel
                    label="Restrict client site to default island (remove islands filter)"
                    control={
                        <Checkbox
                            name="restricetdToOneIsland"
                            color="primary"
                            checked={values.restrictedToOneIsland}
                            onChange={e => {
                                setFieldValue('restrictedToOneIsland', e.target.checked);
                            }}
                        />
                    }
                />
            </Grid>
            <Grid item xs={12} sm={12}>
                <FormControlLabel
                    label="Send all booking emails to the client (and not the guest)"
                    control={
                        <Checkbox
                            color="primary"
                            checked={values.withholdCustomerMail}
                            onChange={e => setFieldValue('withholdCustomerMail', e.target.checked)}
                        />
                    }
                />
            </Grid>
            <Grid item xs={12} sm={12}>
                <FormControlLabel
                    label="Send booking email to the agent as well"
                    control={
                        <Checkbox
                            color="primary"
                            checked={values.agentEmail}
                            onChange={e => setFieldValue('agentEmail', e.target.checked)}
                        />
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    label="Show final price instead of retail during search"
                    control={
                        <Checkbox
                            color="primary"
                            checked={!!values.enable_golf}
                            onChange={e => setFieldValue('enable_golf', !!e.target.checked)}
                        />
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    label="Custom agency discount"
                    control={
                        <Checkbox
                            color="primary"
                            checked={!!values.custom_agency_discount}
                            onChange={e =>
                                setFieldValue('custom_agency_discount', !!e.target.checked)
                            }
                        />
                    }
                />
                <FormControlLabel
                    label="Custom vendor discount"
                    control={
                        <Checkbox
                            color="primary"
                            checked={!!values.custom_vendor_discount}
                            onChange={e =>
                                setFieldValue('custom_vendor_discount', !!e.target.checked)
                            }
                        />
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <FormControlLabel
                    label="Agent commission based on % of Net-Revenue (instead of % of Retail price)"
                    control={
                        <Checkbox
                            color="primary"
                            checked={values.commissionType === 'net_revenue'}
                            onChange={e => {
                                setFieldValue(
                                    'commissionType',
                                    e.target.checked ? 'net_revenue' : 'retail'
                                );
                            }}
                        />
                    }
                />
            </Grid>
            <Grid item xs={12} sm={12}>
                <FormControlLabel
                    label="Send Vouchers without Pricing"
                    control={
                        <Checkbox
                            color="primary"
                            checked={!values.voucherWithPricing}
                            onChange={e => setFieldValue('voucherWithPricing', !e.target.checked)}
                        />
                    }
                />
            </Grid>
        </FormSection>
    );
}
